import React from "react";
import clsx from "clsx";
import {DragDropContext, Draggable, DraggableLocation, Droppable, DropResult} from "@hello-pangea/dnd";
import {ChoiceValue} from "data/models/survey/choice-base";
import {RankOrder_SurveyQuestionPage_Options} from "data/models/survey/rank-order";
import {RenderableTitle} from "data/models/survey/base";
import {QUESTION_CONTAINER_CLASS} from "components/Survey/classes";
import {sortChoiceValues} from "helpers/sort-choice-values";

export type RankOrder_SurveyQuestionPage_Props = {
  options: RankOrder_SurveyQuestionPage_Options,
  title: RenderableTitle,
  description: RenderableTitle | null,
  questionId: string,
  choices: ChoiceValue[],
  value: ChoiceValue[] | undefined,
  onChange: (id: string, value: ChoiceValue[]) => void,
}

export default function RankOrderSurveyQuestionPage(
    {
      options,
      choices,
      title,
      description,
      questionId,
      value,
      onChange
    }: RankOrder_SurveyQuestionPage_Props
) {
  const resultValues = value ?? [];
  const resultValuesKeys = resultValues.map(v => v.key);
  const filteredChoices = choices.filter(c => !resultValuesKeys.includes(c.key));
  const sourceValues = options.sorted ? sortChoiceValues(filteredChoices) : filteredChoices;

  function onDragEnd(result: DropResult) {
    // dropped outside the list
    const {source, destination} = result;
    if (
        !destination
        || (
            destination.droppableId === source.droppableId
            && destination.droppableId === "droppableSource"
        )
        || (
            destination.droppableId !== source.droppableId
            && destination.droppableId === "droppableResult"
            && resultValues.length >= options.maxResultCount
        )
    ) {
      return;
    }

    const dropResult = source.droppableId === destination.droppableId ? reorder(
            source.droppableId === "droppableSource" ? sourceValues : resultValues,
            source.index,
            destination.index,
        )
        : move(
            source.droppableId === "droppableSource" ? sourceValues : resultValues,
            destination.droppableId === "droppableSource" ? sourceValues : resultValues,
            source,
            destination
        );

    onChange(questionId, dropResult);
  }

  function reorder(items: ChoiceValue[], startIndex: number, endIndex: number): ChoiceValue[] {
    const result = Array.from(items);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  }

  function move(
      source: ChoiceValue[],
      destination: ChoiceValue[],
      droppableSource: DraggableLocation,
      droppableDestination: DraggableLocation
  ): ChoiceValue[] {
    const sourceClone = Array.from(source);
    const destClone = Array.from(destination);
    const [removed] = sourceClone.splice(droppableSource.index, 1);

    destClone.splice(droppableDestination.index, 0, removed);

    return droppableSource.droppableId === "droppableResult" ? sourceClone : destClone;
  }

  return (
      <div className={"flex flex-col w-full"}>
        <div className={QUESTION_CONTAINER_CLASS}>
          <fieldset name={questionId} autoFocus={true}>
            <div className={"my-4 text-center"}>{title}</div>
            {description !== null && <div className={"my-6 text-center"}>{description}</div>}
            <DragDropContext onDragEnd={onDragEnd}>
              <div className={"flex flex-row items-start"}>
                <Droppable droppableId="droppableSource">
                  {(provided, snapshot) => (
                      <div
                          className={clsx(
                              "flex flex-col w-1/2 min-h-80 gap-2 p-2 mx-2 md:mx-12 border-2 rounded-3xl border-neutral-50",
                          )}
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                      >
                        {sourceValues.map((choice, index) => (
                            <Draggable key={`${questionId}.${choice.key}`} draggableId={`${questionId}.${choice.key}`}
                                       index={index} isDragDisabled={resultValues.length >= options.maxResultCount}>
                              {(provided, snapshot) => (
                                  <div
                                      id={`${questionId}.${choice.key}`}
                                      className={clsx(
                                          "border-2 px-3 py-0 rounded-full w-full text-center text-neutral-500",
                                          resultValues.length >= options.maxResultCount ? "border-neutral-100" : "border-bc-blue"
                                      )}
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                  >{choice.value}</div>
                              )}
                            </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                  )}
                </Droppable>
                <Droppable droppableId="droppableResult">
                  {(provided, snapshot) => (
                      <div
                          className={clsx(
                              "flex flex-col w-1/2 gap-2 p-2 mx-2 md:mx-12 rounded-3xl bg-neutral-50",
                          )}
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                      >
                        {resultValues.map((choice, index) => (
                            <Draggable key={`${questionId}.${choice.key}`} draggableId={`${questionId}.${choice.key}`}
                                       index={index}>
                              {(provided, snapshot) => (
                                  <div
                                      id={`${questionId}.${choice.key}`}
                                      className={"flex flex-row bg-white border-2 border-bc-blue px-3 py-0 rounded-full w-full text-center"}
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                  >
                                    <div className={"ml-1 text-bc-blue text-xl font-bold"}>{index + 1}</div>
                                    <div className={"grow text-center text-neutral-500"}>{choice.value}</div>
                                  </div>
                              )}
                            </Draggable>
                        ))}
                        {resultValues.length < options.maxResultCount && [...Array(options.maxResultCount - resultValues.length)].map((_, index) =>
                            <div
                                key={index}
                            >
                              <div className={"ml-2 text-transparent text-xl font-bold"}>{index + 1}</div>
                              <div className={"grow text-center text-transparent"}>placeholder</div>
                            </div>)}
                        {provided.placeholder}
                      </div>
                  )}
                </Droppable>
              </div>
            </DragDropContext>
          </fieldset>
        </div>
      </div>
  );
}
